import type { Feed, FeedsClient } from '@stream-io/feeds-client'

const MAX_RESTORE_PAGES = 25

// Only the parts of the Stream feed and client that are used here, so tests can pass small fakes
export interface SynchronizableFeed {
    group: string
    id: string
    currentState: {
        activities?: { id: string }[]
        next?: string
        last_get_or_create_request_config?: Feed['currentState']['last_get_or_create_request_config']
    }
    synchronize: () => Promise<void>
    getNextPage: () => Promise<unknown>
}

export interface FeedFetchingClient {
    getOrCreateFeed: (request: Parameters<FeedsClient['getOrCreateFeed']>[0]) => Promise<{ activities: { id: string }[] }>
}

const hasNewActivities = async (client: FeedFetchingClient, feed: SynchronizableFeed): Promise<boolean> => {
    const { last_get_or_create_request_config: lastRequest, activities = [] } = feed.currentState
    const knownActivityIds = new Set(activities.map(activity => activity.id))

    const response = await client.getOrCreateFeed({
        ...lastRequest,
        next: undefined,
        feed_group_id: feed.group,
        feed_id: feed.id,
        watch: true,
    })

    return response.activities.some(activity => ! knownActivityIds.has(activity.id))
}

/**
 * Resyncs a feed's realtime connection (see useSyncOnWindowFocus). If the first page contains no
 * new activities, nothing in the feed's state changes, so the scroll position is kept. Otherwise
 * the feed is synchronized, which collapses its pagination back to the first page, after which
 * subsequent pages are re-requested until the previous depth is restored (or there's no more to load).
 */
export const synchronizeFeedPreservingPagination = async (client: FeedFetchingClient, feed: SynchronizableFeed): Promise<void> => {
    if (! await hasNewActivities(client, feed)) {
        return
    }

    const activitiesBeforeSync = feed.currentState.activities?.length ?? 0

    await feed.synchronize()

    let pagesRestored = 0

    while (
        (feed.currentState.activities?.length ?? 0) < activitiesBeforeSync
        && feed.currentState.next
        && pagesRestored < MAX_RESTORE_PAGES
    ) {
        await feed.getNextPage()
        pagesRestored += 1
    }
}
